import React from 'react';
import { motion } from 'framer-motion';
import { data } from '@/data';
import SEO from '@/components/SEO';
import { getPageSEO } from '@/config/seoConfig';
import { useTranslation } from '@/hooks/useTranslation';

const About = () => {
  const seoData = getPageSEO('about');
  const t = useTranslation();
  
  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        image={seoData.image}
        type="profile"
      />
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50/30 pt-32 pb-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-playfair text-4xl md:text-5xl lg:text-6xl mb-16 text-gray-800 text-center font-semibold tracking-tight"
        >
          {t.about.title}
        </motion.h1>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Image - Sticky Scroll Effect */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative md:sticky md:top-32 md:self-start"
          >
            <motion.div 
              className="aspect-[3/4] overflow-hidden rounded-2xl shadow-2xl"
              whileInView={{ scale: [0.8, 1] }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <motion.img
                src="/images/João_Xavier_AboutPage.webP"
                alt={data.musician.name}
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                whileInView={{ 
                  filter: ["brightness(0.7)", "brightness(1)"],
                  scale: [0.95, 1]
                }}
                viewport={{ amount: 0.3 }}
              />
            </motion.div>
            
            {/* Decorative parallax elements */}
            <motion.div 
              className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-amber-300/30 to-orange-300/30 rounded-full blur-xl"
              animate={{ 
                y: [0, -20, 0],
                opacity: [0.3, 0.6, 0.3] 
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut" 
              }}
            />
            
            <motion.div 
              className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-tl from-stone-300/20 to-amber-200/20 rounded-full blur-2xl"
              animate={{ 
                y: [0, 15, 0],
                opacity: [0.2, 0.4, 0.2] 
              }}
              transition={{ 
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1 
              }}
            />
          </motion.div>

          {/* Biography Text - Enhanced Scrolling */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8 bg-white/70 backdrop-blur-md p-8 rounded-2xl shadow-xl border border-white/40"
          >
            {t.about.biography.map((paragraph, index) => (
              <div key={index}>
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.15,
                    ease: "easeOut" 
                  }}
                  viewport={{ once: true, amount: 0.3 }}
                  className="font-lora text-gray-700 leading-relaxed text-base md:text-lg tracking-wide relative"
                >
                  <motion.span
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: index * 0.1 + 0.2 }}
                    viewport={{ once: true }}
                    className="block"
                  >
                    {paragraph}
                  </motion.span>
                </motion.p>
                
                {/* Subtle accent line for first paragraph */}
                {index === 0 && (
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "3rem" }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="h-0.5 bg-gradient-to-r from-amber-400 to-orange-400 mt-4 rounded-full"
                  />
                )}
              </div>
            ))}
          </motion.div>
        </div>
        
        {/* Elegant closing section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.5 }}
          viewport={{ once: true, amount: 0.1 }}
          className="mt-24 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.8, delay: 0.8, type: "spring" }}
            viewport={{ once: true }}
            className="w-16 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto"
          />
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default About;